import { t, useLanguage, translateError } from "../../i18n";
import LanguageSelector from '../../components/LanguageSelector';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ChevronRight, Hash, Loader2, Store } from 'lucide-react';
import { toast } from 'sonner';
import { publicAPI } from '../../services/api';
import { useCartStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
export default function EstablishmentEntry() {
  useLanguage();
  const {
    establishmentId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const autoResolvedRef = useRef(false);
  const setTable = useCartStore(state => state.setTable);
  const clearCart = useCartStore(state => state.clearCart);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [establishment, setEstablishment] = useState(null);
  const [tableNumber, setTableNumber] = useState('');
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(true);
  const tableFromQr = searchParams.get('table');
  const resolveTable = useCallback(async (parsedTableNumber, showSuccess = true) => {
    try {
      setSubmitting(true);
      setError('');
      const response = await publicAPI.getTableByNumber(establishmentId, parsedTableNumber);
      const resolvedTable = response.data.table;
      const resolvedEstablishment = response.data.establishment;
      clearCart();
      setTable(resolvedTable.id, resolvedEstablishment.id);
      if (showSuccess) {
        toast.success(t("Table {number} reconnue", {
          number: resolvedTable.table_number
        }));
      }
      navigate('/menu');
    } catch (err) {
      const message = err?.response?.data?.detail === 'Table not found' ? t("Cette table n'existe pas") : err?.response?.data?.detail || t("Cette table n'existe pas");
      setError(message);
      setShowModal(true);
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }, [clearCart, establishmentId, navigate, setTable]);
  useEffect(() => {
    const loadEstablishment = async () => {
      try {
        setLoading(true);
        const response = await publicAPI.getEstablishment(establishmentId);
        setEstablishment(response.data);
      } catch (err) {
        toast.error(t("Impossible de charger l'établissement"));
      } finally {
        setLoading(false);
      }
    };
    if (establishmentId) {
      loadEstablishment();
    }
  }, [establishmentId]);
  useEffect(() => {
    if (showModal && !loading) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [showModal, loading]);
  useEffect(() => {
    if (loading || !establishment || !tableFromQr || autoResolvedRef.current) return;
    const parsedTableNumber = Number(tableFromQr);
    setTableNumber(tableFromQr);
    autoResolvedRef.current = true;
    if (Number.isNaN(parsedTableNumber) || parsedTableNumber <= 0) {
      setError(t("Veuillez entrer un numero de table valide"));
      setShowModal(true);
      return;
    }
    setShowModal(false);
    resolveTable(parsedTableNumber, false);
  }, [establishment, loading, resolveTable, tableFromQr]);
  const handleSubmit = async e => {
    e.preventDefault();
    const parsedTableNumber = Number(tableNumber);
    if (!tableNumber || Number.isNaN(parsedTableNumber) || parsedTableNumber <= 0) {
      setError(t("Veuillez entrer un numéro de table valide"));
      inputRef.current?.focus();
      return;
    }
    resolveTable(parsedTableNumber);
  };
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-6">
        <div className="flex items-center gap-3 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>{t("Chargement...")}</span>
        </div>
      </div>;
  }
  if (!establishment) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-6">
        <div className="w-full max-w-md rounded-[28px] border border-red-200 bg-white p-6 text-center shadow-sm">
          <AlertCircle className="mx-auto mb-4 h-10 w-10 text-red-500" />
          <h1 className="text-xl font-bold text-gray-900" style={{
          fontFamily: 'Manrope'
        }}>{t("Établissement introuvable")}

          </h1>
          <p className="mt-2 text-sm text-gray-500">{t("Le QR code scanné ne correspond à aucun établissement actif.")}

          </p>
        </div>
      </div>;
  }
  return <div className="relative min-h-screen bg-[#F9FAFB]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.10),_transparent_30%),radial-gradient(circle_at_top_left,_rgba(17,24,39,0.06),_transparent_22%)]" />

      <div className="relative flex min-h-screen items-center justify-center px-6 py-10">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-4xl overflow-hidden rounded-[32px] border border-gray-200 bg-white shadow-xl">

          <div className="bg-[#111827] px-8 py-10 text-white">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                <Store className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-blue-400">{t("Bienvenue")}</p>
                <h1 className="text-3xl font-bold" style={{
                fontFamily: 'Manrope'
              }}>
                  {establishment.name}
                </h1>
              </div>
            </div>

            <p className="max-w-2xl text-sm leading-7 text-gray-300">{t("Scannez, indiquez votre table, puis commandez directement depuis votre téléphone.")}

            </p>
          </div>

          <div className="grid gap-4 px-8 py-8 md:grid-cols-3">
            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">{t("1. QR scanné")}</p>
              <p className="mt-1 text-xs leading-6 text-gray-500">{t("Vous êtes bien sur la page d’entrée de l’établissement.")}

              </p>
            </div>

            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">{t("2. Numéro de table")}</p>
              <p className="mt-1 text-xs leading-6 text-gray-500">{t("Renseignez la table indiquée devant vous.")}

              </p>
            </div>

            <div className="rounded-2xl bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">{t("3. Commande")}</p>
              <p className="mt-1 text-xs leading-6 text-gray-500">{t("Accédez au menu et suivez votre commande en temps réel.")}

              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showModal && <motion.div className="mobile-modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }}>

            <motion.div initial={{
          opacity: 0,
          y: 18,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 18,
          scale: 0.98
        }} transition={{
          duration: 0.2
        }} className="mobile-modal-panel w-full max-w-md rounded-[28px] bg-white p-6 shadow-2xl">

              <div className="mb-4 flex justify-end"><LanguageSelector /></div>
              <div className="mb-5">
                <p className="text-sm font-medium text-violet-600">{t("Table requise")}</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900" style={{
              fontFamily: 'Manrope'
            }}>{t("Quel est votre numéro de table ?")}

              </h2>
                <p className="mt-2 text-sm leading-6 text-gray-500">{t("Entrez le numéro de table pour accéder au menu de")}
                {' '}
                  <span className="font-semibold text-gray-700">{establishment.name}</span>.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Hash className="h-4 w-4" />{t("Numéro de table")}

                </label>

                  <Input ref={inputRef} type="number" min="1" inputMode="numeric" placeholder={t("Ex. 12")} value={tableNumber} onChange={e => {
                setTableNumber(e.target.value);
                if (error) setError('');
              }} className="h-12 rounded-2xl border-gray-300 text-base" />

                </div>

                {error ? <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {translateError(error, "Cette table n'existe pas")}
                  </div> : <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">{t("Vous ne connaissez pas votre numéro ? Demandez-le à un membre du staff.")}

              </div>}

                <Button type="submit" disabled={submitting} className="h-12 w-full rounded-full bg-[#111827] text-white hover:bg-black">

                  {submitting ? <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("Vérification...")}

                </> : <>{t("Accéder au menu")}

                  <ChevronRight className="ml-2 h-4 w-4" />
                    </>}
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
